import React, { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import {
    ICreateOrUpdateProjectResponse,
    IGetProject,
    IProjectTemplate,
} from '../../interfaces/project.interface';
import {
    useCreateOrUpdateProjectMutation,
    useLazyGetProjectByIdQuery,
    useLazyGetProjectsQuery,
} from '../../store/api/editor.api';
import { getDesignElements } from '../../store/selector/brand-elements.selector';
import { usePresignTemplateMutation } from '../../store/api/template.api';
import {
    SetEditorTemplateData,
    SetProjectCreationStatus,
    SetRefetchProjects,
    SetTemplateLoadingState,
} from '../../store/slice/editor.slice';
import { addCorrectFontsInTemplate } from '../../utils/template.util';
import { ITemplateFromProject } from '../../interfaces/common.interface';
import { displayErrorToast } from '../../utils/ToastUtils';
import { allowedExtensionsProject } from '../../constants/createFlow';
import { toast } from 'react-toastify';
import { Button, Form, Spinner } from 'react-bootstrap';
import Masonry from 'react-masonry-css';
import ProjectCardLoader from '../../components/project/ProjectCardLoader';
import InfiniteScroll from 'react-infinite-scroll-component';
import ProjectCard from '../../components/project/ProjectCard';
import { useNavigate } from 'react-router-dom';
import { getRefetchProjects } from '../../store/selector/editor.selector';

interface IProps {
    store: any;
}

const Projects = ({ store }: IProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const activeBrand = useAppSelector(getActiveBrand);
    const [currentPage, setCurrentPage] = useState(1);
    const [projects, setProjectsData] = useState<IGetProject[]>([]);
    const [importLoading, setImportLoading] = useState(false);
    const [isCreatingNewProject, setIsCreatingNewProject] = useState(false);
    const [fetchProjectById] = useLazyGetProjectByIdQuery();
    const [
        fetchProjects,
        {
            data: fetchedProjects,
            isFetching: projectsRefetchLoading,
            isLoading: projectsFetchLoading,
        },
    ] = useLazyGetProjectsQuery();
    const [createOrUpdateProject, { isLoading: waiting }] = useCreateOrUpdateProjectMutation();
    const designElementsData = useAppSelector(getDesignElements);
    const refetchProjectsCheck = useAppSelector(getRefetchProjects);
    const [presignTemplate] = usePresignTemplateMutation();

    const [windowHeight, setWindowHeight] = useState(window.innerHeight - 135);
    const breakpointColumnsObj = {
        default: 4,
        1400: 3,
        700: 2,
        500: 1,
    };

    const clearStore = async () => {
        store.clear();
        store.addPage();
        await store.waitLoading();
    };

    const handleNewProject = async () => {
        if (!store || !activeBrand) return;
        const projectName = window.prompt('Enter Project name:');
        if (!projectName) return;

        setIsCreatingNewProject(true);
        dispatch(SetTemplateLoadingState(true));
        dispatch(SetProjectCreationStatus(true));
        await clearStore();
        dispatch(
            SetEditorTemplateData({
                adFromChat: null,
                adFromCreateTab: null,
                templateFromProject: null,
            }),
        );

        try {
            const projectJSON = store.toJSON();
            const res: any = await createOrUpdateProject({
                name: projectName,
                json: addCorrectFontsInTemplate(projectJSON, designElementsData),
                brandId: activeBrand.id,
            });
            const projectData = res.data as ICreateOrUpdateProjectResponse;
            const templateFromProject: ITemplateFromProject = {
                id: projectData.id,
                name: projectData.name,
                templateJSON: projectJSON,
            };
            dispatch(
                SetEditorTemplateData({
                    adFromChat: null,
                    adFromCreateTab: null,
                    templateFromProject,
                }),
            );
            dispatch(SetTemplateLoadingState(false));
            dispatch(SetProjectCreationStatus(false));
            setIsCreatingNewProject(false);
            navigate('/editor');
        } catch (error) {
            setIsCreatingNewProject(false);
            displayErrorToast('Error creating project, please try again later.');
        }
    };

    const createProjectAndUpdateShowInEditor = async (templateData: IProjectTemplate) => {
        try {
            store.loadJSON(templateData.json);
            const res: any = await createOrUpdateProject(templateData);
            const projectData = res.data as ICreateOrUpdateProjectResponse;
            const templateFromProject: ITemplateFromProject = {
                id: projectData.id,
                name: projectData.name,
                templateJSON: templateData.json,
            };
            dispatch(
                SetEditorTemplateData({
                    adFromChat: null,
                    adFromCreateTab: null,
                    templateFromProject,
                }),
            );
        } catch (error) {}
    };

    const handleProjectImport = async (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        if (!store || !activeBrand) return;

        const fileList = event.target.files;
        let jsonData;

        if (fileList && fileList.length > 0) {
            const file = fileList[0];
            const fileExtension = file.name.split('.').pop()?.toLowerCase();
            if (fileExtension && !allowedExtensionsProject.includes(fileExtension)) {
                toast('Only JSON files are allowed.', { type: 'warning' });
                return;
            }

            const fileName = file.name.replace(`.${fileExtension}`, '');
            const reader = new FileReader();

            reader.onload = async (e) => {
                try {
                    setImportLoading(true);
                    dispatch(SetTemplateLoadingState(true));
                    dispatch(SetProjectCreationStatus(true));
                    dispatch(
                        SetEditorTemplateData({
                            adFromChat: null,
                            adFromCreateTab: null,
                            templateFromProject: null,
                        }),
                    );
                    const fileContent = e?.target?.result;
                    jsonData = JSON.parse(fileContent as string);
                    const payload = {
                        template: jsonData,
                    };
                    const response: any = await presignTemplate(payload);
                    if (response && response.data) {
                        jsonData = response.data;
                    }
                    await createProjectAndUpdateShowInEditor({
                        name: fileName,
                        json: jsonData,
                        brandId: activeBrand.id,
                    });
                    setImportLoading(false);
                    dispatch(SetTemplateLoadingState(false));
                    dispatch(SetProjectCreationStatus(false));
                    navigate('/editor');
                } catch (error) {
                    setImportLoading(false);
                    dispatch(SetTemplateLoadingState(false));
                    dispatch(SetProjectCreationStatus(false));
                    console.error('Error parsing JSON data:', error);
                }
            };
            reader.readAsText(file);
        }
    };

    const fetchMoreData = () => {
        setCurrentPage((prev) => prev + 1);
    };

    const handleProjectClick = async (project: IGetProject) => {
        dispatch(SetTemplateLoadingState(true));
        navigate('/editor');
        try {
            const res: any = await fetchProjectById(project.id);
            const projectData = res.data;
            store.loadJSON(projectData.json);
            const templateFromProject: ITemplateFromProject = {
                id: projectData.id,
                name: projectData.name,
                templateJSON: projectData.json,
            };
            dispatch(
                SetEditorTemplateData({
                    adFromChat: null,
                    adFromCreateTab: null,
                    templateFromProject,
                }),
            );

            dispatch(SetTemplateLoadingState(false));
        } catch (error) {
            displayErrorToast('Error fetching project, please try again later.');
            dispatch(SetTemplateLoadingState(false));
        }
    };

    const handleRefetchProjects = async () => {
        if (currentPage === 1 && activeBrand) {
            await fetchProjects({
                page: currentPage,
                brandId: activeBrand.id,
            });
        } else setCurrentPage(1);
    };

    useEffect(() => {
        if (fetchedProjects && currentPage === 1) {
            setProjectsData(fetchedProjects.records);
        } else if (fetchedProjects && currentPage > 1) {
            setProjectsData((prevProjects) => {
                const newProjectsMap = new Map(
                    fetchedProjects.records.map((project) => [project.id, project]),
                );
                const updatedProjects = prevProjects.map(
                    (project) => newProjectsMap.get(project.id) || project,
                );
                const newProjects = fetchedProjects.records.filter(
                    (project) => !prevProjects.some((prevProject) => prevProject.id === project.id),
                );
                return [...updatedProjects, ...newProjects];
            });
        }
    }, [fetchedProjects]);

    useEffect(() => {
        if (currentPage > 0 && activeBrand) {
            fetchProjects({
                page: currentPage,
                brandId: activeBrand.id,
            });
        }
    }, [currentPage, fetchProjects, activeBrand]);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeBrand]);

    useEffect(() => {
        if (refetchProjectsCheck) {
            dispatch(SetRefetchProjects(false));
            handleRefetchProjects();
        }
    }, [refetchProjectsCheck]);

    return (
        <div className="tw-w-full tw-flex tw-flex-col tw-pt-2 tw-min-w-5xl tw-px-5">
            <div className="project-header mt-3 w-100">
                <h2 className="text-dark fw-semibold"></h2>
                <div className="project-header_button">
                    <Form.Group className="form-group">
                        <Button
                            variant="outline-primary"
                            size="lg"
                            onClick={() => {
                                const fileInput = document.getElementById('fileInput');
                                if (fileInput) {
                                    fileInput.click();
                                }
                            }}
                        >
                            {importLoading ? (
                                <Spinner
                                    as="span"
                                    className="mr-1 pe-none"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                    }}
                                />
                            ) : (
                                'Import'
                            )}
                        </Button>
                        <input
                            id="fileInput"
                            type="file"
                            className="file-input"
                            onChange={handleProjectImport}
                        />
                    </Form.Group>

                    <Button variant="primary" onClick={handleNewProject} size="lg">
                        {isCreatingNewProject || waiting ? (
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                        ) : (
                            <>
                                <i className="ph ph-plus sm"></i> New Project
                            </>
                        )}
                    </Button>
                    {!projectsFetchLoading && (
                        <Button
                            variant="primary"
                            className="iconbtn rounded-circle"
                            style={{ width: '2.6rem', height: '2.6rem' }}
                            onClick={handleRefetchProjects}
                        >
                            {projectsRefetchLoading ? (
                                <Spinner style={{ width: '1.5rem', height: '1.5rem' }} />
                            ) : (
                                <i
                                    className="ph ph-arrows-clockwise"
                                    style={{ fontSize: '20px' }}
                                ></i>
                            )}
                        </Button>
                    )}
                </div>
            </div>
            <div className="project-container mt-3">
                {projectsFetchLoading ? (
                    <div className="d-flex flex-column gap-2 justify-content-center align-items-center mt-4">
                        <Masonry
                            breakpointCols={breakpointColumnsObj}
                            className="pixii-grid project-contents mx-2"
                            columnClassName="pixii-grid_column mx-2"
                        >
                            {' '}
                            {Array.from({ length: 8 }).map((_, index) => (
                                <div key={index} className=" mb-4">
                                    <ProjectCardLoader />
                                </div>
                            ))}
                        </Masonry>
                    </div>
                ) : projects && projects.length > 0 ? (
                    <>
                        <InfiniteScroll
                            dataLength={projects.length}
                            next={fetchMoreData}
                            hasMore={fetchedProjects ? fetchedProjects.hasNext : false}
                            loader={
                                <div
                                    className="w-100 d-flex justify-content-center align-item-center"
                                    style={{ height: '100px', padding: '24px' }}
                                >
                                    <Spinner
                                        as="span"
                                        className="mr-1 pe-none"
                                        role="status"
                                        aria-hidden="true"
                                        animation="border"
                                        style={{
                                            marginTop: '40px',
                                            width: '48px',
                                            height: '48px',
                                            color: '#967AA7',
                                            background: 'transparent',
                                        }}
                                    />
                                </div>
                            }
                            height={windowHeight}
                            endMessage={
                                <p
                                    style={{ textAlign: 'center' }}
                                    className="text-primary fw-light"
                                >
                                    <b>Yay! You have seen it all</b>
                                </p>
                            }
                        >
                            <Masonry
                                breakpointCols={breakpointColumnsObj}
                                className="pixii-grid project-contents mx-2"
                                columnClassName="pixii-grid_column mx-1"
                            >
                                {projects.map((project, index) => (
                                    <div key={index} className=" mb-4">
                                        <ProjectCard
                                            store={store}
                                            project={project}
                                            projects={projects}
                                            index={index}
                                            projectsFetchLoading={projectsFetchLoading}
                                            setCurrentPage={setCurrentPage}
                                            handleProjectClick={handleProjectClick}
                                        />
                                    </div>
                                ))}
                            </Masonry>
                        </InfiniteScroll>
                    </>
                ) : (
                    <h1>No Project</h1>
                )}
            </div>
        </div>
    );
};

export default Projects;
